import { useCallback, useEffect, useState } from "react";

import { send } from "../api/auth";
import { console_ } from "../api/console";
import { expenses, type EventExpenses } from "../api/expenses";
import { may } from "../api/auth";
import { StatusChip } from "../design/StatusChip";
import { Tabs } from "../design/Tabs";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import { MileageRate } from "./Expenses";

// ---------------------------------------------------------------------------
// SWEEP S20. THE ORGANISATION'S OWN DETAILS, WHICH NOTHING COULD CHANGE.
//
// Every organisation ran on Europe/London from sign-up for good — the country was
// right and the clock was wrong, from the same form — and could not correct a typo in
// its own name. The clock is not cosmetic: every time on every screen is drawn in it,
// and Risk Radar's daypart factor is scored on it.
// docs/FINDING-no-shipped-client-can-set-an-organisations-clock-2026-08-30.md.
// ---------------------------------------------------------------------------

/** Every IANA zone this browser knows, with the organisation's own kept even if it does not. */
function zones(current: string): string[] {
  const all = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.("timeZone") ?? [];
  return all.includes(current) ? all : [current, ...all];
}

/** "Budapest (UTC+1)": the city part of the zone and its offset now, readable in any language. */
function zoneLabel(zone: string, locale: string): string {
  const city = (zone.split("/").pop() ?? zone).replace(/_/g, " ");
  try {
    const offset = new Intl.DateTimeFormat(locale, { timeZone: zone, timeZoneName: "shortOffset" })
      .formatToParts(new Date())
      .find((p) => p.type === "timeZoneName")?.value;
    return offset ? `${city} (${offset})` : city;
  } catch {
    return zone;
  }
}

/** Every currency this browser knows, with the organisation's own kept even if it does not. */
function currencies(current: string, locale: string): { code: string; label: string }[] {
  const all = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.("currency") ?? [];
  const codes = current && !all.includes(current) ? [current, ...all] : all;
  const names = new Intl.DisplayNames([locale], { type: "currency" });
  const collator = new Intl.Collator(locale);
  return codes
    .map((code) => ({ code, label: `${names.of(code) ?? code} (${code})` }))
    .sort((a, b) => collator.compare(a.label, b.label));
}

export function OrgSettings() {
  const { apiBase, me, reload } = useSession();
  const { t, refusal, locale } = useT();
  const org = me?.authenticated ? me.active_org : null;
  const [name, setName] = useState("");
  const [timezone, setTimezone] = useState("");
  const [currency, setCurrency] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: "live" | "failed"; text: string } | null>(null);

  useEffect(() => {
    if (!org) return;
    setName(org.name);
    setTimezone(org.timezone ?? "");
    setCurrency(org.currency ?? "");
  }, [org]);

  if (!org) return <p className="muted">{t("Loading…")}</p>;

  const changed = name.trim() !== org.name || timezone !== org.timezone || currency !== org.currency;

  async function save() {
    setBusy(true);
    setMessage(null);
    try {
      const { status, body } = await send<{ error?: { message?: string } }>(
        console_.updateOrganisation(apiBase, { name: name.trim(), timezone, currency: currency.toUpperCase() }),
      );
      if (status !== 200) {
        setMessage({ tone: "failed", text: refusal(body.error) ?? t("These details could not be saved.") });
        return;
      }
      await reload();
      setMessage({ tone: "live", text: t("Saved. Every time in the console is now shown on this clock.") });
    } catch {
      setMessage({ tone: "failed", text: t("We could not reach Verbunk. Check your connection and try again.") });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Tabs
      label={t("Organisation")}
      tabs={[
        {
          id: "details",
          label: t("Details"),
          content: (
    <div className="stack">
    <section className="panel">
      <div className="panel-head">
        <h2>{t("Organisation")}</h2>
      </div>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <div className="form-grid">
          <div className="field">
            <label htmlFor="org-name">{t("Name")}</label>
            <input id="org-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
          </div>
          <div className="field">
            <label htmlFor="org-timezone">{t("Timezone")}</label>
            <select id="org-timezone" value={timezone} onChange={(e) => setTimezone(e.target.value)}>
              {zones(org.timezone ?? "").map((z) => (
                <option key={z} value={z}>
                  {zoneLabel(z, locale)}
                </option>
              ))}
            </select>
            <p className="field-hint">
              {t("Shift times, deadlines and the working-time day are all read on this clock. Your browser is on {zone}.", {
                zone: zoneLabel(Intl.DateTimeFormat().resolvedOptions().timeZone, locale),
              })}
            </p>
          </div>
          <div className="field">
            <label htmlFor="org-currency">{t("Currency")}</label>
            <select id="org-currency" value={currency} onChange={(e) => setCurrency(e.target.value)}>
              {currencies(org.currency ?? "", locale).map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
            <p className="field-hint">{t("The currency new events and invoices start in.")}</p>
          </div>
        </div>
        {message ? (
          <div className="form-message" role="status" aria-live="polite">
            <StatusChip tone={message.tone} label={message.tone === "live" ? t("Saved") : t("Not saved")} />
            <span>{message.text}</span>
          </div>
        ) : null}
        <div className="row">
          <button type="submit" className="btn" disabled={busy || !changed}>
            {busy ? t("Saving…") : t("Save")}
          </button>
        </div>
      </form>
    </section>
    <NamesSetting />
    </div>
          ),
        },
        { id: "plan-billing", label: t("Plan and billing"), content: <BillingSetting /> },
        { id: "reminders", label: t("Shift reminders"), content: <ReminderSetting /> },
        { id: "mileage", label: t("Mileage rate"), content: <MileageSetting /> },
      ]}
    />
  );
}

// ---------------------------------------------------------------------------
// Tour T-20 / 0325 (wording approved by the owner as DRAFT-09, 22 October 2026). Lists of people
// show an email; an organisation may choose to show names instead. The explanation is the
// approved wording, and the choice is the organisation's own, so it is stated beside the switch.
// ---------------------------------------------------------------------------
function NamesSetting() {
  const { apiBase, me, reload } = useSession();
  const { t, refusal } = useT();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: "live" | "failed"; text: string } | null>(null);
  if (!me?.authenticated || !may(me, "org.manage")) return null;
  const on = me.active_org?.show_people_names === true;

  async function choose(next: boolean) {
    setBusy(true);
    setMessage(null);
    try {
      const { status, body } = await send<{ error?: { message?: string } }>(console_.updateOrganisation(apiBase, { show_people_names: next }));
      if (status !== 200) {
        setMessage({ tone: "failed", text: refusal(body.error) ?? t("This could not be saved.") });
        return;
      }
      await reload();
      setMessage({ tone: "live", text: next ? t("Lists now show names.") : t("Lists now show emails only.") });
    } catch {
      setMessage({ tone: "failed", text: t("We could not reach Verbunk. Check your connection and try again.") });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel stack">
      <div className="panel-head">
        <h2>{t("People in lists")}</h2>
      </div>
      <label className="check">
        <input type="checkbox" checked={on} disabled={busy} onChange={(e) => void choose(e.target.checked)} />
        {t("Show people's names in lists")}
      </label>
      <p className="muted">
        {t("By default, lists of people show an email address and not a name. Showing fewer personal details to fewer people is how we keep to the rule that you should hold and display only what the work needs.")}
      </p>
      <p className="muted">
        {t("If you turn this on, the people lists in your organisation show each person's name where one has been given. Turning it on is your organisation's own decision, and your organisation is responsible for it: it decides who in your team may see people's names, and it must be able to explain why to the people concerned. A person can always change their own name in their account. You can turn this off again at any time, and names are then hidden from the lists again.")}
      </p>
      {message ? (
        <div className="form-message" role="status" aria-live="polite">
          <StatusChip tone={message.tone} label={message.tone === "live" ? t("Saved") : t("Not saved")} />
          <span>{message.text}</span>
        </div>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// P4-C10 / 0277. THE PLAN. Bought on Stripe's Checkout, changed or cancelled on
// Stripe's portal; the plan shown is what the webhook recorded, never what the
// button was pressed for. Hidden without org.manage, as ReminderSetting.
// ---------------------------------------------------------------------------
type Billing = {
  enabled: boolean;
  plan: "trial" | "crew_manager" | "agency_plus";
  plans: { id: "crew_manager" | "agency_plus"; monthly_eur: number }[];
  subscription: { status: string } | null;
  charges: {
    ref: string; currency: string; amount: string; period_start: string | null; created_at: string; state: string;
    hub_number: string | null; hub_error?: string | null; hub_credit_number?: string | null;
    nav?: { status: string; label: string; environment: string | null } | null;
  }[];
};
const PLAN_LABEL = { trial: "Trial", crew_manager: "Crew Manager", agency_plus: "Agency Plus" } as const;
const PAID = new Set(["active", "trialing", "past_due"]);

function BillingSetting() {
  const { apiBase } = useSession();
  const { t, refusal, locale } = useT();
  const [data, setData] = useState<Billing | null>(null);
  const [busy, setBusy] = useState(false);
  // D88-22. An individual is invoiced as a private person; asked before Stripe, every time.
  const [buyerKind, setBuyerKind] = useState<"" | "business" | "individual">("");
  // D88-32. A business's tax number for the invoice; the API checks its shape.
  const [taxNumber, setTaxNumber] = useState("");
  const [message, setMessage] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get("billing") === "done"
      ? t("Thank you. The plan changes as soon as Stripe confirms the payment.")
      : null,
  );

  useEffect(() => {
    void (async () => {
      const { status, body } = await send<Billing>(console_.billing(apiBase));
      if (status === 200 && Array.isArray(body.plans)) setData(body);
    })();
  }, [apiBase]);

  if (!data) return null;
  const paid = data.subscription !== null && PAID.has(data.subscription.status);

  // D88-24. The PDF comes through Verbunk's API (the hub holds it); saved as the file the hub named.
  async function pdf(ref: string, which: "pdf" | "credit-pdf", number: string) {
    setMessage(null);
    try {
      const spec = console_.billingPdf(apiBase, ref, which);
      const res = await fetch(spec.url, { method: spec.method, headers: spec.headers, credentials: spec.credentials });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: { message?: string } };
        setMessage(refusal(body.error) ?? t("The invoice could not be downloaded."));
        return;
      }
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = `${number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setMessage(t("We could not reach Verbunk. Check your connection and try again."));
    }
  }

  async function go(request: ReturnType<typeof console_.openBillingPortal>) {
    setBusy(true);
    setMessage(null);
    try {
      const { status, body } = await send<{ url?: string; error?: { message?: string } }>(request);
      if (status === 200 && body.url) {
        window.location.assign(body.url);
        return;
      }
      setMessage(refusal(body.error) ?? t("Stripe could not be opened."));
    } catch {
      setMessage(t("We could not reach Verbunk. Check your connection and try again."));
    }
    setBusy(false);
  }

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t("Plan and billing")}</h2>
      </div>
      <div className="stack">
        <p>{t("Current plan: {plan}", { plan: t(PLAN_LABEL[data.plan] ?? "Trial") })}</p>
        <p className="field-hint">{t("Agency Plus adds analytics, event margins and Proof Packs. Crew Manager and the trial keep everything else.")}</p>
        {data.subscription?.status === "past_due" ? <p>{t("Payment overdue. Update the card from Manage billing.")}</p> : null}
        {!data.enabled ? (
          <p className="field-hint">{t("Billing is not switched on yet.")}</p>
        ) : (
          <>
            {!paid ? (
              <fieldset className="field">
                <legend>{t("Who is buying?")}</legend>
                <label>
                  <input type="radio" name="buyer-kind" checked={buyerKind === "business"} onChange={() => setBuyerKind("business")} />{" "}
                  {t("A business")}
                </label>
                <label>
                  <input type="radio" name="buyer-kind" checked={buyerKind === "individual"} onChange={() => setBuyerKind("individual")} />{" "}
                  {t("A private person")}
                </label>
                {buyerKind === "business" ? (
                  <div className="field">
                    <label htmlFor="billing-tax-number">{t("Tax number")}</label>
                    <input id="billing-tax-number" inputMode="numeric" placeholder="12345678-1-23" maxLength={13}
                      value={taxNumber} onChange={(e) => setTaxNumber(e.target.value)} />
                    <p className="field-hint">{t("It goes on the invoice. A Hungarian business must give it.")}</p>
                  </div>
                ) : null}
              </fieldset>
            ) : null}
            <div className="row">
              {paid ? (
                <button type="button" className="btn" disabled={busy} onClick={() => void go(console_.openBillingPortal(apiBase))}>
                  {busy ? t("Opening Stripe…") : t("Manage billing")}
                </button>
              ) : (
                data.plans.map((p) => (
                  <button key={p.id} type="button" className="btn" disabled={busy || buyerKind === ""} onClick={() => void go(console_.startCheckout(apiBase, p.id, buyerKind, buyerKind === "business" ? taxNumber.trim() : null))}>
                    {t("{plan}, €{price} a month", { plan: t(PLAN_LABEL[p.id]), price: p.monthly_eur })}
                  </button>
                ))
              )}
            </div>
            <p className="field-hint">{t("You pay on Stripe's own page. Change the card, switch plan or cancel from Manage billing.")}</p>
          </>
        )}
        {message ? (
          <div className="form-message" role="status" aria-live="polite">
            <span>{message}</span>
          </div>
        ) : null}
        {data.charges.length > 0 ? (
          <>
            <h3>{t("Subscription payments")}</h3>
            <ul className="stack">
              {data.charges.map((c) => (
                <li key={c.ref}>
                  {(c.period_start ?? c.created_at.slice(0, 10))} · {c.currency} {c.amount} ·{" "}
                  {(c.state === "invoiced" || c.state === "credited") && c.hub_number ? (
                    <>
                      {t("Invoice {number}", { number: c.hub_number })}{" "}
                      <button type="button" className="btn btn-quiet" onClick={() => void pdf(c.ref, "pdf", c.hub_number!)}>
                        {t("Download PDF")}
                      </button>
                      {c.hub_credit_number ? (
                        <>
                          {" · "}
                          {t("Cancelled by credit note {number}", { number: c.hub_credit_number })}{" "}
                          <button type="button" className="btn btn-quiet" onClick={() => void pdf(c.ref, "credit-pdf", c.hub_credit_number!)}>
                            {t("Download PDF")}
                          </button>
                        </>
                      ) : null}
                      {c.nav ? <> · {translateLabel(locale, c.nav.label)}</> : null}
                    </>
                  ) : c.state === "invoiced" && c.hub_number
                    ? t("Invoice {number}", { number: c.hub_number })
                    : c.state === "test_mode"
                      ? t("Test payment, no invoice")
                      : c.state === "hub_refused"
                        ? t("Invoice held ({code})", { code: c.hub_error ?? "" })
                        : t("Invoice on its way")}
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sweep §4 / 0200. WHEN WORKERS ARE REMINDED OF A SHIFT.
//
// A number of hours, or off. Nothing sends reminders by itself until the owner
// rules who may act when nobody is signed in, so the hint says where they are
// queued: on each event's Messages screen. Its own Save, because it is its own
// audit row and its own door (app.set_shift_reminder_hours).
// ---------------------------------------------------------------------------
function ReminderSetting() {
  const { apiBase } = useSession();
  const { t, refusal } = useT();
  const [saved, setSaved] = useState<number | null | undefined>(undefined);
  const [leadHours, setLeadHours] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: "live" | "failed"; text: string } | null>(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      const { status, body } = await send<{ hours_before?: number | null }>(console_.reminderSetting(apiBase));
      if (!live || status !== 200 || body.hours_before === undefined) return;
      setSaved(body.hours_before);
      setLeadHours(body.hours_before === null ? "" : String(body.hours_before));
    })();
    return () => {
      live = false;
    };
  }, [apiBase]);

  // Not readable (no org.manage) or still loading: nothing to offer.
  if (saved === undefined) return null;

  const next = leadHours.trim() === "" ? null : Number(leadHours);
  const changed = next !== saved;

  async function save() {
    setBusy(true);
    setMessage(null);
    try {
      const { status, body } = await send<{ hours_before?: number | null; error?: { message?: string } }>(
        console_.updateReminderSetting(apiBase, next),
      );
      if (status !== 200) {
        setMessage({ tone: "failed", text: refusal(body.error) ?? t("The reminder setting could not be saved.") });
        return;
      }
      setSaved(body.hours_before ?? null);
      setMessage({
        tone: "live",
        text: next === null
          ? t("Saved. Workers will not be reminded.")
          : t("Saved. Shifts due a reminder show on each event's Messages screen."),
      });
    } catch {
      setMessage({ tone: "failed", text: t("We could not reach Verbunk. Check your connection and try again.") });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t("Shift reminders")}</h2>
      </div>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <div className="field">
          <label htmlFor="org-reminder-hours">{t("Remind workers this many hours before a shift")}</label>
          <input
            id="org-reminder-hours"
            type="number"
            inputMode="numeric"
            min={1}
            max={168}
            step={1}
            value={leadHours}
            onChange={(e) => setLeadHours(e.target.value)}
          />
          <p className="field-hint">
            {t("Leave it empty for no reminders. Each booked shift is reminded once; standby is not. Reminders are queued from an event's Messages screen — nothing sends them on its own yet.")}
          </p>
        </div>
        {message ? (
          <div className="form-message" role="status" aria-live="polite">
            <StatusChip tone={message.tone} label={message.tone === "live" ? t("Saved") : t("Not saved")} />
            <span>{message.text}</span>
          </div>
        ) : null}
        <div className="row">
          <button type="submit" className="btn" disabled={busy || !changed}>
            {busy ? t("Saving…") : t("Save reminder setting")}
          </button>
        </div>
      </form>
    </section>
  );
}

// ---------------------------------------------------------------------------
// S144. THE MILEAGE RATE, WHERE AN ORGANISATION'S SETTINGS ARE. It is one rate
// for the whole agency, and it could be reached only from inside an event's
// Expenses screen. The panel is that screen's own; it saves through the same
// POST /v1/expenses/mileage-rate and reads the rate from its GET.
// ---------------------------------------------------------------------------
function MileageSetting() {
  const { apiBase } = useSession();
  const [data, setData] = useState<Pick<EventExpenses, "units" | "mileage"> | null>(null);

  const load = useCallback(async () => {
    const { status, body } = await send<Partial<EventExpenses>>(expenses.mileageRate(apiBase));
    // Not readable (no org.manage): nothing to offer, as ReminderSetting.
    if (status === 200 && Array.isArray(body.units)) setData({ units: body.units, mileage: body.mileage ?? null });
  }, [apiBase]);

  useEffect(() => {
    void load();
  }, [load]);

  return data ? <MileageRate data={data} onSaved={load} /> : null;
}
